import { useTranslation } from 'react-i18next';
import {
  ExternalProjectContact,
  InternalProjectContact,
  ProjectMember,
  ProjectMemberCalculatedData,
  ProjectMemberSearchResultItem,
} from '../../../models/ProjectContacts';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducerState, getUserMe } from '../../../apps/main/rootReducer';
import { ProjectRole } from '../../../models/Types';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  fetchHourlyRates,
  updateExternalProjectContact,
  updateInternalProjectContactForController,
  updateProjectContacts,
} from '../../projects/actions';
import {
  Button,
  Divider,
  Drawer,
  InformationBox,
  Select,
  TextArea,
  Toggle,
} from '@prio365/prio365-react-library';
import { Form, notification } from 'antd';
import UserAvatar from '../../../components/UserAvatar';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import {
  ProjectExtension,
  ProjectExtensionAccess,
  ProjectExtensionType,
} from '../../../models/ProjectExtension';
import {
  apiFetchProjectExtensionsForProject,
  apiUpdateProjectExtensionAccesses,
} from '../../projects/api';
import SvgIcon from '../../../components/SvgIcon';
import { getSvgIconPathByType } from '../../../util/icon';
import JobTitleSelect from './JobTitleSelect';
import useContactsContext from '../hooks/useContactsProvider';
import { createSelector } from 'reselect';
import { DataState } from '../../projects/reducers/hourlyRates';
import useFilterContext from '../../../components/Filter/hooks/useFilterContext';
import { ApiResult } from '../../../api';
import { GenericSearchResultItem } from '../../../components/Filter/types';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { useNavigate } from 'react-router-dom';
import useProjectsContext from '../../projects/hooks/useProjectsContext';

const hourlyRatesSelector = createSelector<
  [(state: RootReducerState) => DataState],
  DataState
>(
  (state) => state.projects.hourlyRates.data,
  (hourlyRates) => hourlyRates
);

interface ProjectContactsDrawerProps {
  visible: boolean;
  projectId: string;
  projectMember: ProjectMemberSearchResultItem;
  setOpenDrawer: (open: boolean) => void;
}

interface initialValues {
  jobTitle: string;
  roles: string[];
  hourlyRate: string;
  notes?: string;
}

const ProjectContactsDrawer: React.FC<ProjectContactsDrawerProps> = (props) => {
  //#region ------------------------------ Defaults
  const { visible, projectId, projectMember, setOpenDrawer } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const theme = useTheme<PrioTheme>();
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState<initialValues | null>(
    null
  );

  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [editTabkey, setEditTabKey] = useState<string>('general');
  const [loading, setLoading] = useState<boolean>(false);
  const { optimisticWrite } = useFilterContext<
    ProjectMember,
    ProjectMemberCalculatedData
  >();

  const [projectExtensions, setProjectExtensions] = useState<
    ProjectExtension[]
  >([]);

  const [projectExtensionAccesses, setProjectExtensionAccesses] = useState<
    ProjectExtensionAccess[]
  >([]);

  const hourlyRatesByProjectId = useSelector(hourlyRatesSelector);

  const hourlyRates = useMemo(() => {
    return hourlyRatesByProjectId[projectId] ?? [];
  }, [hourlyRatesByProjectId, projectId]);

  const userMe = useSelector(getUserMe);
  const { getProjectById } = useProjectsContext();
  const project = getProjectById(projectId);
  const projectInternalOfficeId = project?.internalOfficeId;
  const myProjectRoles: ProjectRole[] = useMemo(() => {
    return userMe?.prioData.projectRoles[projectId] || [];
  }, [userMe, projectId]);
  const isGlobalAdmin =
    userMe.prioData.globalRoles.includes('globalAdmin') || false;
  const isOfficeAdmin =
    userMe.prioData.officeRoles?.[projectInternalOfficeId]?.includes(
      'officeAdmin'
    ) || false;

  const { getContactById } = useContactsContext();

  const { getCompanyById } = useCompaniesContext();

  const projectMemberContact = useMemo(() => {
    return getContactById(projectMember?.data?.contactId);
  }, [projectMember?.data?.contactId, getContactById]);

  const hourlyRateOptions = useMemo(() => {
    const hourlyRateOptions = hourlyRates?.map((i) => ({
      label: i.name,
      value: i.hourlyRateId,
    }));
    return hourlyRateOptions;
  }, [hourlyRates]);

  const roleOptions = useMemo(() => {
    const pojectRoles: ProjectRole[] = [
      'projectAdmin',
      'projectAssistance',
      'projectController',
      'projectMember',
    ];
    return pojectRoles?.map((i) => ({
      label: t(`contacts:projectContactsPage.projectRoles.${i}`),
      value: i,
      disabled: i === 'projectMember' ? true : false,
    }));
  }, [t]);

  //#endregion

  //#region ------------------------------ Effects

  useEffect(() => {
    if (visible && projectMember) {
      const initialHourlyRate = hourlyRates.find(
        (rate) => rate.hourlyRateId === projectMember?.data?.hourlyRateId
      )
        ? projectMember?.data?.hourlyRateId
        : '';
      const initialValues: initialValues = {
        jobTitle: projectMember?.data?.jobTitle,
        roles: projectMember?.data?.projectRoles,
        hourlyRate: initialHourlyRate,
        notes: projectMember?.data?.notes,
      };
      setInitialValues(initialValues);
      form.setFieldsValue(initialValues);
      setIsChanged(false);
    }
  }, [visible, projectMember, form, hourlyRates, myProjectRoles]);

  useEffect(() => {
    if (
      (visible && myProjectRoles.includes('projectController')) ||
      myProjectRoles.includes('projectAdmin') ||
      myProjectRoles.includes('projectAssistance')
    ) {
      dispatch(fetchHourlyRates(projectId));
    }
  }, [dispatch, projectId, visible, myProjectRoles]);

  useEffect(() => {
    const fetchData = async () => {
      if (visible && editTabkey === 'addins') {
        const { data } = await apiFetchProjectExtensionsForProject(projectId);
        if (data) {
          setProjectExtensions(data);
        }
      }
    };
    fetchData();
  }, [visible, editTabkey, projectId]);

  useEffect(() => {
    if (projectMember && projectMember.data && visible) {
      setProjectExtensionAccesses(projectMember.data.projectExtensionAccesses);
    }
  }, [projectMember, visible]);

  //#endregion

  //#region ------------------------------ Methods / Handlers

  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (initialValues) {
      const isFormChanged =
        JSON.stringify(initialValues) !== JSON.stringify(allValues);
      setIsChanged(isFormChanged);
    }
  };

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleFinish = async (values: initialValues) => {
    setLoading(true);
    const updatedItem = {
      ...projectMember.data,
      jobTitle: values.jobTitle,
      projectRoles: values.roles as ProjectRole[],
      notes: values.notes,
    };
    const rollbackItem = {
      ...projectMember.data,
      jobTitle: projectMember.data.jobTitle,
      projectRoles: projectMember.data.projectRoles,
      notes: projectMember.data.notes,
    } as InternalProjectContact | ExternalProjectContact;

    let updatedItem2: InternalProjectContact | null = null;
    let rollbackItem2: InternalProjectContact | null = null;

    // Check if hourlyRate has changed
    if (
      values.hourlyRate &&
      values.hourlyRate !== projectMember.data.hourlyRateId &&
      myProjectRoles.includes('projectController') &&
      !myProjectRoles.includes('projectAdmin')
    ) {
      updatedItem2 = {
        ...projectMember.data,
        hourlyRateId: values.hourlyRate,
      } as InternalProjectContact;
      rollbackItem2 = {
        ...projectMember.data,
        hourlyRateId: projectMember.data.hourlyRateId,
      } as InternalProjectContact;
      await optimisticWrite(
        [
          {
            data: {
              ...projectMember.data,
              hourlyRateId: values.hourlyRate,
              jobTitle: values.jobTitle,
              projectRoles: values.roles as ProjectRole[],
            },
            calculated: projectMember.calculated,
            method: 'update',
          },
        ],
        async (): Promise<
          ApiResult<
            GenericSearchResultItem<
              ProjectMember,
              ProjectMemberCalculatedData
            >[]
          >
        > => {
          const response = await (dispatch as any)(
            updateInternalProjectContactForController(
              projectId,
              [updatedItem2],
              [rollbackItem2]
            )
          );
          if (response.status >= 200 && response.status < 300) {
            return {
              result: response,
              data: [
                {
                  data: {
                    ...projectMember.data,
                    hourlyRateId: values.hourlyRate as string,
                    jobTitle: values.jobTitle,
                    projectRoles: values.roles as ProjectRole[],
                  },
                  calculated: projectMember.calculated,
                },
              ],
            };
          } else {
            notification.open({
              message: t('common:error'),
              description: t('contacts:projectContactsPage.messageError'),
            });
            return {
              result: response,
              data: [],
            };
          }
        }
      );
      await delay(1000);
    }

    if (
      (values.jobTitle !== projectMember.data.jobTitle ||
        JSON.stringify(values.roles) !==
          JSON.stringify(projectMember.data.projectRoles) ||
        myProjectRoles.includes('projectAdmin') ||
        myProjectRoles.includes('projectAssistance')) &&
      projectMember.data.externalProjectContactId === null
    ) {
      await optimisticWrite(
        [
          {
            data: {
              ...projectMember.data,
              hourlyRateId: values.hourlyRate as string,
              projectRoles: values.roles as ProjectRole[],
              jobTitle: values.jobTitle,
              notes:
                projectMember.data.externalProjectContactId !== null
                  ? values.notes
                  : '',
            },
            calculated: projectMember.calculated,
            method: 'update',
          },
        ],
        async () => {
          const response = await (dispatch as any)(
            updateProjectContacts(projectId, [updatedItem], [rollbackItem])
          );
          if (response.status >= 200 && response.status < 300) {
            return {
              result: response,
              data: [
                {
                  data: {
                    ...projectMember.data,
                    hourlyRateId: values.hourlyRate as string,
                    jobTitle: values.jobTitle,
                    projectRoles: values.roles as ProjectRole[],
                    notes:
                      projectMember.data.externalProjectContactId !== null
                        ? values.notes
                        : '',
                  },
                  calculated: projectMember.calculated,
                },
              ],
            };
          } else {
            notification.open({
              message: t('common:error'),
              description: t('contacts:projectContactsPage.messageError'),
            });
            return {
              result: response,
              data: [],
            };
          }
        }
      );
    }
    if (
      (values.notes !== projectMember.data.notes ||
        values.jobTitle !== projectMember.data.jobTitle) &&
      projectMember.data.externalProjectContactId !== null
    ) {
      const updatedItem3 = {
        jobTitle: values.jobTitle,
        notes: values.notes,
        externalProjectContactId: projectMember.data.externalProjectContactId,
      } as ExternalProjectContact;
      const rollbackItem3 = {
        ...projectMember.data,
        notes: projectMember.data.notes,
        externalProjectContactId: projectMember.data.externalProjectContactId,
      } as ExternalProjectContact;
      await optimisticWrite(
        [
          {
            data: {
              ...projectMember.data,
              hourlyRateId: values.hourlyRate as string,
              projectRoles: values.roles as ProjectRole[],
              jobTitle: values.jobTitle,
              notes: values.notes,
            },
            calculated: projectMember.calculated,
            method: 'update',
          },
        ],
        async () => {
          const response = await (dispatch as any)(
            updateExternalProjectContact(
              projectId,
              [updatedItem3],
              [rollbackItem3]
            )
          );
          if (response.status >= 200 && response.status < 300) {
            return {
              result: response,
              data: [
                {
                  data: {
                    ...projectMember.data,
                    hourlyRateId: values.hourlyRate as string,
                    jobTitle: values.jobTitle,
                    projectRoles: values.roles as ProjectRole[],
                    notes: values.notes,
                  },
                  calculated: projectMember.calculated,
                },
              ],
            };
          } else {
            notification.open({
              message: t('common:error'),
              description: t('contacts:projectContactsPage.messageError'),
            });
            return {
              result: response,
              data: [],
            };
          }
        }
      );
    }
    setLoading(false);
    setOpenDrawer(false);
  };

  const handleArchiveButtonClicked = () => {
    const updatedItem = {
      ...projectMember.data,
      isArchived: true,
    };
    const rollbackItem = {
      ...projectMember.data,
      isArchived: false,
    } as InternalProjectContact | ExternalProjectContact;

    const iAmRemoved = projectMember.data.contactId === userMe.id;

    optimisticWrite(
      [
        {
          data: { ...projectMember.data, isArchived: true },
          calculated: projectMember.calculated,
          method: 'update',
        },
      ],
      async (): Promise<
        ApiResult<
          GenericSearchResultItem<ProjectMember, ProjectMemberCalculatedData>[]
        >
      > => {
        const response = await (dispatch as any)(
          updateProjectContacts(projectId, [updatedItem], [rollbackItem])
        );
        if (response.status >= 200 && response.status < 300) {
          return {
            result: response as unknown as Response,
            data: [
              {
                data: {
                  ...projectMember.data,
                  isArchived: true,
                },
                calculated: projectMember.calculated,
              },
            ],
          };
        } else {
          notification.open({
            message: t('common:error'),
            description:
              response.statusText ||
              t('contacts:projectContactsPage.messageError'),
          });
          return {
            result: response as Response,
            data: null,
          };
        }
      }
    );
    setOpenDrawer(false);
    if (iAmRemoved) navigate('/module/prio/projects/me/dashboard/');
  };

  const handleArchiveExternalButtonClicked = () => {
    const updatedItem = {
      ...projectMember.data,
      isArchived: true,
    } as ExternalProjectContact;
    const rollbackItem = {
      ...projectMember.data,
      isArchived: false,
    } as ExternalProjectContact;
    optimisticWrite(
      [
        {
          data: { ...projectMember.data, isArchived: true },
          calculated: projectMember.calculated,
          method: 'remove',
        },
      ],
      async () => {
        const response = await (dispatch as any)(
          updateExternalProjectContact(projectId, [updatedItem], [rollbackItem])
        );
        if (response.status >= 200 && response.status < 300) {
          return {
            result: response,
            data: [
              {
                data: {
                  ...projectMember.data,
                  isArchived: true,
                },
                calculated: projectMember.calculated,
              },
            ],
          };
        } else {
          notification.open({
            message: t('common:error'),
            description:
              response.statusText ||
              t('contacts:projectContactsPage.messageError'),
          });
          return {
            result: response,
            data: [],
          };
        }
      }
    );
    setOpenDrawer(false);
  };

  //#endregion

  //#region ------------------------------ Tabs

  const renderGeneralTabContent = (): ReactNode => {
    return (
      <Form
        initialValues={initialValues}
        form={form}
        onValuesChange={handleValuesChange}
        onFinish={handleFinish}
        layout="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <div>
          <Form.Item
            name="jobTitle"
            label={t('contacts:projectContactsPage.columns.jobTitle')}
          >
            <JobTitleSelect
              disabled={
                !(
                  myProjectRoles.includes('projectAdmin') ||
                  myProjectRoles.includes('projectAssistance')
                ) && projectMember?.data?.externalProjectContactId === null
              }
              size="default"
            />
          </Form.Item>
          {!projectMember?.data?.externalProjectContactId && (
            <>
              {/* Disabled weil be call das nicht handelt */}
              <Form.Item
                name="hourlyRate"
                label={t('contacts:projectContactsPage.columns.hourlyRate')}
                style={{
                  display:
                    myProjectRoles.includes('projectAdmin') ||
                    myProjectRoles.includes('projectController')
                      ? 'flex'
                      : 'none',
                }}
              >
                <div>
                  <Select
                    showSearch={false}
                    options={hourlyRateOptions}
                    disabled
                  ></Select>
                  <InformationBox
                    type="warning"
                    description={t('contacts:projectContactsPage.warning')}
                    marginBottom="none"
                    marginTop="small"
                  />
                </div>
              </Form.Item>
              <Form.Item
                name="roles"
                label={t('contacts:projectContactsPage.columns.projectRoles')}
              >
                <Select
                  showSearch={false}
                  options={roleOptions}
                  mode="tags"
                  disabled={
                    !myProjectRoles.includes('projectAdmin') &&
                    !myProjectRoles.includes('projectAssistance')
                  }
                ></Select>
              </Form.Item>
            </>
          )}
          {projectMember?.data?.externalProjectContactId && (
            <Form.Item
              name="notes"
              label={t('contacts:projectContactsPage.columns.notes')}
            >
              <TextArea
                placeholder={t('contacts:projectContactsPage.noNotes')}
                style={{
                  padding: `${theme.spacing.small}px ${theme.spacing.regular}px`,
                }}
                rows={5}
              />
            </Form.Item>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isGlobalAdmin ||
          isOfficeAdmin ||
          myProjectRoles.includes('projectAdmin') ||
          myProjectRoles.includes('projectAssistance') ||
          projectMember?.data?.externalProjectContactId ? (
            <Button
              type="link"
              style={{ color: theme.colors.base.red.default }}
              onClick={
                projectMember?.data?.internalProjectContactId !== null ||
                isGlobalAdmin ||
                isOfficeAdmin
                  ? handleArchiveButtonClicked
                  : handleArchiveExternalButtonClicked
              }
            >
              {t('contacts:projectContactsPage.actions.archive')}
            </Button>
          ) : (
            <div></div>
          )}

          <Form.Item style={{ marginBottom: '0px' }}>
            <Button type="default" onClick={() => setOpenDrawer(false)}>
              {t('common:cancel')}
            </Button>

            <Button
              htmlType="submit"
              disabled={!isChanged || loading}
              style={{ marginInline: theme.spacing.small }}
              loading={loading}
            >
              {t('common:save')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    );
  };

  const renderAddInsTabContent = (): ReactNode => {
    return (
      <div>
        {projectExtensions.map((extension, index) => (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SvgIcon
                  key={index}
                  width={40}
                  height={40}
                  path={getSvgIconPathByType(extension.projectExtensionType)}
                />
                <div key={index}>
                  {extension.projectExtensionType
                    .charAt(0)
                    .toLocaleUpperCase() +
                    extension.projectExtensionType.slice(1)}
                </div>
              </div>
              <Toggle
                checked={projectExtensionAccesses.some(
                  (ext) =>
                    ext.projectExtensionId === extension.projectExtensionId &&
                    ext.shouldHaveAccess === true
                )}
                onChange={async (checked) => {
                  const updatedAccess = {
                    projectExtensionId: extension.projectExtensionId,
                    shouldHaveAccess: checked,
                    contactId: projectMember.data.contactId,
                  };
                  optimisticWrite(
                    [
                      {
                        data: {
                          ...projectMember.data,
                          projectExtensionAccesses:
                            projectExtensionAccesses.map((ext) =>
                              ext.projectExtensionId ===
                              extension.projectExtensionId
                                ? { ...ext, shouldHaveAccess: checked }
                                : ext
                            ) as ProjectExtensionAccess[],
                        },
                        calculated: {
                          ...projectMember.calculated,
                          projectExtensionAccessProjectExtensionMaps:
                            projectExtensionAccesses.map((ext) => {
                              const projectExtension = projectExtensions.find(
                                (pe) =>
                                  pe.projectExtensionId ===
                                  ext.projectExtensionId
                              );
                              return {
                                ...ext,
                                projectExtensionType:
                                  projectExtension?.projectExtensionType as ProjectExtensionType,
                                shouldHaveAccess:
                                  ext.projectExtensionId ===
                                  extension.projectExtensionId
                                    ? checked
                                    : ext.shouldHaveAccess,
                              };
                            }),
                        },
                        method: 'update',
                      },
                    ],
                    async (): Promise<
                      ApiResult<
                        GenericSearchResultItem<
                          ProjectMember,
                          ProjectMemberCalculatedData
                        >[]
                      >
                    > => {
                      const response = await apiUpdateProjectExtensionAccesses(
                        projectId,
                        [updatedAccess]
                      );
                      if (
                        response.result.status >= 200 &&
                        response.result.status < 300
                      ) {
                        setProjectExtensionAccesses((prevAccesses) =>
                          prevAccesses.map((ext) =>
                            ext.projectExtensionId ===
                            extension.projectExtensionId
                              ? { ...ext, shouldHaveAccess: checked }
                              : ext
                          )
                        );

                        return {
                          result: response.result,
                          data: [
                            {
                              data: {
                                ...projectMember.data,
                                projectExtensionAccesses:
                                  projectExtensionAccesses.map((ext) =>
                                    ext.projectExtensionId ===
                                    extension.projectExtensionId
                                      ? { ...ext, shouldHaveAccess: checked }
                                      : ext
                                  ) as ProjectExtensionAccess[],
                              },
                              calculated: {
                                ...projectMember.calculated,
                                projectExtensionAccessProjectExtensionMaps:
                                  projectExtensionAccesses.map((ext) => {
                                    const projectExtension =
                                      projectExtensions.find(
                                        (pe) =>
                                          pe.projectExtensionId ===
                                          ext.projectExtensionId
                                      );
                                    return {
                                      ...ext,
                                      projectExtensionType:
                                        projectExtension?.projectExtensionType as ProjectExtensionType,
                                      shouldHaveAccess:
                                        ext.projectExtensionId ===
                                        extension.projectExtensionId
                                          ? checked
                                          : ext.shouldHaveAccess,
                                    };
                                  }),
                              },
                            },
                          ],
                        };
                      } else {
                        notification.open({
                          message: t('common:error'),
                          description: t(
                            'contacts:projectContactsPage.messageError'
                          ),
                        });
                        setProjectExtensionAccesses((prevAccesses) =>
                          prevAccesses.map((ext) =>
                            ext.projectExtensionId ===
                            extension.projectExtensionId
                              ? { ...ext, shouldHaveAccess: !checked }
                              : ext
                          )
                        );
                        return {
                          result: response.result,
                          data: [],
                        };
                      }
                    }
                  );
                }}
              ></Toggle>
            </div>
            <Divider type="horizontal" />
          </>
        ))}
      </div>
    );
  };
  //#endregion

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserAvatar
            contact={projectMemberContact}
            backgroundColor={theme.colors.base.blue.default}
            size="medium"
          ></UserAvatar>
          <div style={{ marginLeft: theme.spacing.small }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '2px',
              }}
            >
              {projectMemberContact?.firstName +
                ' ' +
                projectMemberContact?.lastName}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '400' }}>
              {getCompanyById(projectMemberContact?.companyId)?.fullName}
            </div>
          </div>
        </div>
      }
      visible={visible}
      tabs={[
        {
          key: 'general',
          label: t('contacts:projectContactsPage.drawerTabs.general'),
          content: renderGeneralTabContent(),
        },
        ...(myProjectRoles.includes('projectAdmin') &&
        projectMember?.data?.externalProjectContactId === null
          ? [
              {
                key: 'addins',
                label: t('contacts:projectContactsPage.drawerTabs.addIns'),
                content: renderAddInsTabContent(),
              },
            ]
          : []),
      ]}
      onClose={() => {
        setOpenDrawer(false);
        setInitialValues(null);
      }}
      closable
      defaultTab={
        projectMember?.data?.externalProjectContactId === null
          ? editTabkey
          : 'general'
      }
      onTabChange={(key) => setEditTabKey(key)}
    ></Drawer>
  );
};
export default ProjectContactsDrawer;
